import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Checkbox,
} from "@mui/material";
import { WebMapContext } from "../../../pages/sections/web-map/WebMapGoogleMap";
import { MS_GEOHASH_MARGINS_TYPES } from "../../../constants/Data";

function getColorByValue(colorArray, value, field) {

  if(colorArray && colorArray.length){
  const color= colorArray.map((item) => {
    if (item.sectionType === "MS_MAIN_TABLE") {
      const foundColor = item.data.find(
        (colorObj) => String(colorObj.colorValue) === String(value)
      );
      return foundColor ? foundColor.color : "#FFFFFF";
    } else if (item.sectionType === "MS_GEO_TABLE") {
      const foundColor = item.data.find(
        (colorObj) => MS_GEOHASH_MARGINS_TYPES[String(colorObj.type)]  === String(field)
      );
      return foundColor ? (value === 1 ? foundColor.upColor : foundColor.downColor) : "#FFFFFF";
    } 
  });
  return color[0] || "#FFFFFF"
}else{
  return "#FFFFFF"
}
  
}

const newShade = (hexColor, magnitude) => {
  hexColor = hexColor.replace(`#`, ``);
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return hexColor;
  }
};

// ColorDot component for rendering color dots
const ColorDot = ({ array, value, field }) => (
  <div
    style={{
      width: "15px",
      height: "15px",
      borderRadius: "50%",
      backgroundColor: getColorByValue(array, value, field), // Light-colored fill
      border: `2px solid ${newShade(getColorByValue(array, value, field), -50)}`,
    }}
  />
);

const ColorDash = () => (
  <div
    style={{
      width: "12px",
      height: "1px",
      backgroundColor: "#FFFF00", // Light-colored fill
      border: `2px solid ${newShade("#FFFF00", -50)}`,
    }}
  />
);

// Dropdown component for rendering dropdowns
const Dropdown = ({ options, value }) => (
  <select>
    {options.map((option, index) => (
      <option key={index} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

// Checkbox component for rendering checkboxes
const CustomCheckbox = ({ checked }) => (
  <input type="checkbox" checked={checked} />
);

const getTypeComponent = (type) => {
  switch (type) {
    case "colorDot":
      return ColorDot;
    case "dropdown":
      return Dropdown;
    case "checkbox":
      return CustomCheckbox;
    default:
      return null;
  }
};

const CommonInfiniteScrollTable = ({
  onFetchMoreData,
  columns,
  data,
  onClickRow = () => {},
  initialFetch = true,
  onSelectRow,
  onSelectAll,
  customPage = null,
  setCustomPage = () => {},
  height,
  enableSorting = true,
  onClearSelectedRows = () => {},
  sorting = {sortBy: null, sortOrder: null},
  setSorting = () => {},
  parentDeselect,
  setParentDeselect
}) => {
  const [hasMore, setHasMore] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const tableContainerRef = useRef();
  const container = tableContainerRef.current;

  const { msTableColorConfig } = useContext(WebMapContext);

  useEffect(() => {
    setHasMore(true);
    // Initial data fetch set data
    if (initialFetch) {
      setSelectAll(false);
      if (container && !customPage) {
        container.scrollTop = 0;
      }
      setItems(data);
      setPage(2);
    }
  }, [JSON.stringify(data), initialFetch]);

  useEffect(() => {
    // Mannually deselect all selected data from parent component
    if (parentDeselect) {
      if (selectAll) setSelectAll(false);
      const arr = items.map((n) => ({ ...n, selected: false }));
      setItems(arr);
      onSelectAll([]);
      setParentDeselect(false);
    }
  }, [parentDeselect]);

  const fetchMoreData = (pageNum, sorting, order, sortFetch) => {
    onFetchMoreData({ page: pageNum, sortBy: sorting, sortOrder: order, sortFetch }).then(
      (newItems) => {
        if (sortFetch){
          container.scrollTop = 0; 
          setSelectAll(false);
        } 
        sortFetch ?  setItems([...newItems?.data]) :  setItems([...items, ...newItems?.data]);
        if (newItems?.data?.length === 0) {
          setHasMore(false);
        } else {
          customPage
            ? newItems?.page
              ? setCustomPage(newItems.page)
              : setCustomPage(customPage + 1)
            : setPage(pageNum + 1);
        }
      }
    );
  };

  const handleScroll = () => {
    if (
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - 20 &&
      hasMore &&
      (customPage ? customPage !== 1 : page !== 1)
    ) {
      fetchMoreData(customPage ? customPage : page, sorting.sortBy, sorting.sortOrder, false);
    }
  };

  const handleOnClickRow = (e, row) => {
    onClickRow(e, row, tableContainerRef);
  };

  const onClickCheckbox = (index) => {
    const rows = [...items];
    rows[index] = {
      ...rows[index],
      selected: rows[index]?.selected ? !rows[index].selected : true,
    };
    setItems(rows);
    onSelectRow(rows[index]);
  };

  const onSelectAllClick = () => {
    setSelectAll(!selectAll);
    const arr = items.map((n) => ({ ...n, selected: !selectAll }));
    setItems(arr);
    onSelectAll(!selectAll ? arr : []);
    return;
  };

  const isSelected = (row) => row?.selected || false;

  const handleSort = (column) => {
    if (!enableSorting) return;
    onClearSelectedRows();
    let tSorting = sorting.sortBy;
    let order = sorting.sortOrder;
    let columnField = column.sortName || column.field

    if (sorting.sortBy === columnField) {
      order = sorting.sortOrder === "asc" ? "desc" : "asc"
      setSorting({...sorting, sortOrder: order})
    } else {
      order = "asc";
      tSorting = columnField;
      setSorting({sortBy: tSorting, sortOrder: "asc"})
    }
    fetchMoreData(1, tSorting, order, true);
  };

  return (
    <TableContainer
      component={Paper}
      style={{
        overflowX: "auto",
        overflowY: "auto",
        height: `${height}px`,
      }}
      ref={tableContainerRef}
      onScroll={handleScroll}
    >
      <Table size="small" stickyHeader={true}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                checked={selectAll}
                onChange={onSelectAllClick}
                sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                inputProps={{
                  "aria-label": "select all",
                }}
              />
            </TableCell>
            {columns.map((column, index) => {
              if (column.colSpan === 0) {
                return null; // Skip rendering for merged columns
              }
              return (
                <TableCell
                  key={index}
                  colSpan={column.colSpan || 1}
                  onClick={() => handleSort(column)}
                  style={{
                    padding: "3px 6px",
                    fontSize: "11px",
                    textAlign: column.textAlign || "left",
                    cursor: "pointer",
                    minWidth: column.minWidth || 0
                  }}
                >
                  {column.label}
                  {column.hideSort ? <></> : enableSorting  && sorting.sortBy === (column.sortName || column.field) ? (
                    sorting.sortOrder === "asc" ? (
                      <span style={{ color: "black" }}>▲</span>
                    ) : (
                      <span style={{ color: "black" }}>▼</span>
                    )
                  ) : (
                    <span style={{ color: "gray", opacity: 0.5 }}>▼</span>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              style={{
                backgroundColor: isSelected(row) ? "#dcf4e4" : "inherit",
              }}
            >
              <TableCell
                padding="checkbox"
                onClick={() => onClickCheckbox(rowIndex)}
              >
                <Checkbox
                  color="primary"
                  checked={isSelected(row)}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                  inputProps={{
                    "aria-labelledby": `enhanced-table-checkbox-${rowIndex}`,
                  }}
                />
              </TableCell>
              {columns.map((column, colIndex) => {
                const Component = getTypeComponent(column.type);
                const prevColumnValue =
                  colIndex > 0 ? row[columns[colIndex - 1].field] : null;
                const prevColumnType =
                  colIndex > 0 ? columns[colIndex - 1].type : null;
                return row[column.field] === -1 &&
                  column.type === "colorDot" ? (
                  <></>
                ) : (
                  <TableCell
                    key={colIndex}
                    colSpan={
                      prevColumnValue === -1 && prevColumnType === "colorDot"
                        ? 2
                        : column.colSpan === 0
                        ? -1
                        : 1
                    }
                    style={{
                      padding: "2px 1px",
                      fontSize: "11px",
                      textAlign:
                        prevColumnValue === -1 && prevColumnType === "colorDot"
                          ? "center"
                          : (column.textAlign || "left"),
                    }}
                    onClick={(e) => handleOnClickRow(e, row)}
                  >
                    {Component ? (
                      column.type === "colorDot" ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "flex-end",
                            height: "100%",
                          }}
                        >
                          {(row[column.valueField]==='' || row[column.valueField]===null || row[column.valueField]===undefined) ?
                          <ColorDash/>
                          :<Component
                          array={msTableColorConfig}
                          value={row[column.field]}
                          field={column.field}
                        />}
                        </div>
                      ) : (
                        <Component
                          array={msTableColorConfig}
                          value={row[column.field]}
                          field={column.field}
                        />
                      )
                    ) : column.round ? ( // roundoff
                      parseFloat(row[column.field]).toFixed(column.round)
                    ) : (
                      row[column.field] ? row[column.field] : "-"
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default CommonInfiniteScrollTable;
